import React from "react";
import { router } from "@inertiajs/react";
import styled from "styled-components";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useCart, useCartSummary, useOrderActions } from "../contexts/order";
import { useRoom } from "../contexts/room";
import { getKitchenStatus } from "../pages/hebe/hgs/fnb/mock";
import { fnbTaxRupiah } from "../util";
import { fallbackMenuImage, swapInFallbackImage } from "./menu-item";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 3;
`;

const DrawerContainer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 28rem;
  background-color: #fff;
  z-index: 4;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
  border-left: 1px solid #e5e5e5;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e5e5;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const GuestInfo = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 0.5rem;
  background: none;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: #999;
  padding: 2rem 0;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CartItem = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ItemThumbnail = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 10px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const ItemDescription = styled.p`
  font-size: 0.75rem;
  color: #888;
  margin: 2px 0 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItemPrice = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0;
`;

const DeleteButton = styled.button`
  padding: 0.5rem;
  background: none;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  color: #ef4444;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

const ItemFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ItemNote = styled.textarea`
  width: 100%;
  min-height: 52px;
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  padding: 0.5rem;
  font-size: 0.8125rem;
  color: #555;
  resize: vertical;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f0f0f0;
  border-radius: 0.5rem;
  padding: 0.25rem;
`;

const QuantityButton = styled.button`
  padding: 0.5rem;
  min-width: 36px;
  min-height: 36px;
  background: none;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }

  &:active {
    background-color: #d5d5d5;
  }
`;

const QuantityDisplay = styled.span`
  width: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
`;

const ItemTotal = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #c9a961;
`;

const Summary = styled.div`
  border-top: 2px solid #c9a961;
  background-color: #faf8f3;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;

  ${(props) =>
    props.$isTotal &&
    `
    padding-top: 0.5rem;
    border-top: 1px solid #e5e5e5;
  `}
`;

const SummaryLabel = styled.span`
  color: ${(props) => (props.$bold ? "#333" : "#666")};
  font-weight: ${(props) => (props.$bold ? "600" : "400")};
`;

const SummaryValue = styled.span`
  color: ${(props) => (props.$accent ? "#c9a961" : "#333")};
  font-weight: ${(props) => (props.$bold ? "600" : "500")};
  font-size: ${(props) => (props.$large ? "1.125rem" : "0.875rem")};
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #c9a961;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #b89451;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #c9a961;
  }
`;

// Intl.NumberFormat construction is one of the more expensive browser calls, and this used to run
// once per item per render inside a closure rebuilt every render. Built once, reused everywhere.
const RUPIAH = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 });
const formatAmount = (amount) =>
  `Rp${RUPIAH.format(Math.round(Number(amount || 0)))}`;

const NOTE_DEBOUNCE_MS = 300;

// One row, memoized, owning its own note state.
//
// Typing here used to write to the cart on EVERY keystroke, which republished the order context and
// re-rendered all 90 menu items behind the drawer. Now the keystroke touches only this row, and the
// cart is updated once the guest pauses — the same debounce the menu card already used.
const CartRow = React.memo((props) => {
  const {
    item,
    onRemove,
    onQuantityChange,
    onNoteChange,
    eachLabel,
    notePlaceholder,
  } = props;
  const [localNote, setLocalNote] = React.useState(item.note || "");
  const debounceRef = React.useRef(null);
  const pendingRef = React.useRef(null);

  const incomingNote = item.note || "";
  React.useEffect(() => {
    if (!debounceRef.current) setLocalNote(incomingNote);
  }, [incomingNote]);

  // Kept in a ref so the unmount cleanup below can stay on `[]` deps and still call the current
  // callback. Without it the cleanup would close over the first render's props.
  const flushRef = React.useRef(null);
  flushRef.current = () => {
    if (pendingRef.current === null) return;
    const value = pendingRef.current;
    pendingRef.current = null;
    onNoteChange(item.id, value);
  };

  // Closing the drawer within the debounce window would otherwise throw the note away, so commit
  // whatever is still pending on the way out.
  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      flushRef.current();
    },
    [],
  );

  const handleNote = React.useCallback(
    (event) => {
      const value = event.target.value;
      setLocalNote(value);
      pendingRef.current = value;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        pendingRef.current = null;
        onNoteChange(item.id, value);
      }, NOTE_DEBOUNCE_MS);
    },
    [item.id, onNoteChange],
  );

  // Commit on blur as well as on the pause, so the value is in the cart before focus can reach the
  // menu card's own buffer for the same line.
  const commitNote = React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    flushRef.current();
  }, []);

  // Each row binds its own id, so the parent can hand down callbacks that never change identity.
  const handleRemove = React.useCallback(
    () => onRemove(item.id),
    [item.id, onRemove],
  );
  const handleDecrease = React.useCallback(
    () => onQuantityChange(item.id, item.quantity - 1),
    [item.id, item.quantity, onQuantityChange],
  );
  const handleIncrease = React.useCallback(
    () => onQuantityChange(item.id, item.quantity + 1),
    [item.id, item.quantity, onQuantityChange],
  );

  const unitPrice = Number(item?.price?.amount || 0);
  const lineTotal = React.useMemo(
    () => formatAmount(unitPrice * item.quantity),
    [unitPrice, item.quantity],
  );

  return (
    <CartItem>
      <ItemHeader>
        <ItemThumbnail
          src={item.image || fallbackMenuImage}
          alt={item.name}
          onError={swapInFallbackImage}
          loading="lazy"
        />
        <ItemInfo>
          <ItemName>{item.name}</ItemName>
          {item.description && (
            <ItemDescription>{item.description}</ItemDescription>
          )}
          <ItemPrice>
            {item?.price?.formattedText || formatAmount(unitPrice)} {eachLabel}
          </ItemPrice>
        </ItemInfo>
        <DeleteButton onClick={handleRemove} aria-label={`Remove ${item.name}`}>
          <Trash2 size={16} />
        </DeleteButton>
      </ItemHeader>

      <ItemFooter>
        <QuantityControl>
          <QuantityButton
            onClick={handleDecrease}
            aria-label="Decrease quantity"
          >
            <Minus size={14} />
          </QuantityButton>
          <QuantityDisplay>{item.quantity}</QuantityDisplay>
          <QuantityButton
            onClick={handleIncrease}
            aria-label="Increase quantity"
          >
            <Plus size={14} />
          </QuantityButton>
        </QuantityControl>
        <ItemTotal>{lineTotal}</ItemTotal>
      </ItemFooter>

      <ItemNote
        value={localNote}
        onChange={handleNote}
        onBlur={commitNote}
        maxLength={200}
        placeholder={notePlaceholder}
      />
    </CartItem>
  );
});
CartRow.displayName = "CartRow";

const KitchenClosedNote = styled.div`
  color: #a1152d;
  font-size: 12px;
  margin-bottom: 8px;
  text-align: center;
`;

export const CartDrawer = (props) => {
  const { onClose, building_code } = props;
  const { t } = useLanguage();
  const { roomNumber, guestName } = useRoom();
  const cart = useCart();
  const { cartTotal } = useCartSummary();
  const { removeFromCart, updateCartQuantity, updateCartItemNote } =
    useOrderActions();
  const [isKitchenClosed, setIsKitchenClosed] = React.useState(false);
  const [taxRateBps, setTaxRateBps] = React.useState(0);
  const [taxLabel, setTaxLabel] = React.useState("");

  React.useEffect(() => {
    let active = true;
    getKitchenStatus().then((status) => {
      if (!active || !status) return;
      if (!status.is_open) setIsKitchenClosed(true);
      setTaxRateBps(Number(status.fnb_tax_rate_bps || 0));
      setTaxLabel(status.fnb_tax_label || "");
    });
    return () => {
      active = false;
    };
  }, []);

  const handleCheckout = React.useCallback(() => {
    if (isKitchenClosed) return;
    onClose();
    router.visit(`/hebe/${building_code}/hgs/checkout`);
  }, [isKitchenClosed, onClose, building_code]);

  // These three never change identity, which is what lets every CartRow stay memoized.
  const handleRemove = React.useCallback(
    (itemId) => removeFromCart(itemId),
    [removeFromCart],
  );
  const handleQuantityChange = React.useCallback(
    (itemId, quantity) => updateCartQuantity(itemId, quantity),
    [updateCartQuantity],
  );
  const handleNoteChange = React.useCallback(
    (itemId, note) => updateCartItemNote(itemId, note),
    [updateCartItemNote],
  );

  const { subtotal, tax, total } = React.useMemo(() => {
    const taxAmount = fnbTaxRupiah(cartTotal, taxRateBps);
    return {
      subtotal: cartTotal,
      tax: taxAmount,
      total: cartTotal + taxAmount,
    };
  }, [cartTotal, taxRateBps]);

  return (
    <>
      <Overlay onClick={onClose} />

      <DrawerContainer>
        <Header>
          <HeaderContent>
            <Title>{t.order.cart.title}</Title>
            {guestName && roomNumber && (
              <GuestInfo>
                {guestName} • {t.room.roomNumber} {roomNumber}
              </GuestInfo>
            )}
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {cart.length === 0 ? (
            <EmptyMessage>{t.order.cart.empty}</EmptyMessage>
          ) : (
            <ItemList>
              {cart.map((item) => (
                <CartRow
                  key={item.id}
                  item={item}
                  onRemove={handleRemove}
                  onQuantityChange={handleQuantityChange}
                  onNoteChange={handleNoteChange}
                  eachLabel={t.order.cart.each}
                  notePlaceholder={t.order.itemNotePlaceholder}
                />
              ))}
            </ItemList>
          )}
        </Content>

        {cart.length > 0 && (
          <Summary>
            <SummaryList>
              <SummaryRow>
                <SummaryLabel>{t.order.cart.subtotal}</SummaryLabel>
                <SummaryValue>{formatAmount(subtotal)}</SummaryValue>
              </SummaryRow>
              {/* The row the drawer always had; it reads "Pajak / Tax 7%" once the tax is active. */}
              <SummaryRow>
                <SummaryLabel>
                  {taxRateBps > 0 && taxLabel ? taxLabel : t.order.cart.tax}
                </SummaryLabel>
                <SummaryValue>{formatAmount(tax)}</SummaryValue>
              </SummaryRow>
              <SummaryRow $isTotal>
                <SummaryLabel $bold>{t.order.cart.total}</SummaryLabel>
                <SummaryValue $bold $large $accent>
                  {formatAmount(total)}
                </SummaryValue>
              </SummaryRow>
            </SummaryList>

            {isKitchenClosed && (
              <KitchenClosedNote>{t.kitchen.closedCheckout}</KitchenClosedNote>
            )}
            <CheckoutButton onClick={handleCheckout} disabled={isKitchenClosed}>
              {isKitchenClosed ? t.kitchen.closed : t.order.cart.checkout}
            </CheckoutButton>
          </Summary>
        )}
      </DrawerContainer>
    </>
  );
};
